/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC boundary; see mem://preferences/lint-debt.md */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, ArrowUpRight, Copy, Edit, Image as ImageIcon, Trash2 } from "lucide-react";
import { useWorkspace } from "@/hooks/useWorkspace";
import { duplicateExcerpt } from "@/lib/excerpts";
import { extractSearchTokens, highlightHtml, highlightText } from "@/lib/highlight";
import { deriveLibraryTitle, resolveReturnToHref } from "@/lib/library";
import { LibraryCategoryBadge } from "@/components/library/LibraryCategoryBadge";
import { AppSeo } from "@/components/AppSeo";
import { ReadingFontToggle } from "@/components/reading/ReadingFontToggle";
import { safeHref } from "@/lib/safeHref";

const ExcerptDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { active } = useWorkspace();
  const [item, setItem] = useState<any>(null);
  const [duplicating, setDuplicating] = useState(false);

  const tokens = useMemo(
    () => extractSearchTokens(searchParams.get("q") ?? ""),
    [searchParams]
  );

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await supabase.from("excerpts")
        .select("*, library_items(id, title, subtitle, display_title, author_text, published_year, library_item_categories(library_categories(id, name, color, workspace_id, created_at, updated_at))), excerpt_tags(tags(name)), excerpt_topics(content_framework_topics(label))")
        .eq("id", id).single();
      if (!data) { setItem(null); return; }
      // Notes live in notes_encrypted; fetch decrypted plaintext via RPC.
      const workspaceId = (data as any).workspace_id as string | undefined;
      let notes: string | null = null;
      if (workspaceId) {
        const { data: decrypted } = await (supabase as any).rpc("get_decrypted_excerpts", {
          _workspace_id: workspaceId,
          _ids: [id],
        });
        notes = ((decrypted ?? []) as Array<{ id: string; notes: string | null }>)[0]?.notes ?? null;
      }
      setItem({ ...data, notes });
    })();
  }, [id]);

  const remove = async () => {
    if (!confirm("Delete this excerpt?")) return;
    const { error } = await supabase.from("excerpts").delete().eq("id", id!);
    if (error) return toast.error(error.message);
    toast.success("Deleted"); navigate("/excerpts");
  };

  const useInImageStudio = () => {
    if (!item) return;
    sessionStorage.setItem("excerptus.imageStudioPrefill", JSON.stringify({
      bodyHtml: item.body_html,
      author: item.library_items?.author_text ?? "",
      publication: item.library_items?.display_title ?? item.library_items?.title ?? "",
    }));
    navigate("/image-studio");
  };

  if (!item) return <div className="text-muted-foreground">Loading…</div>;
  const tags = (item.excerpt_tags ?? []).map((et: any) => et.tags?.name).filter(Boolean);
  const topics = (item.excerpt_topics ?? []).map((et: any) => et.content_framework_topics?.label).filter(Boolean);
  const sourceRecord = item.library_items ?? null;
  const inheritedCategories = (sourceRecord?.library_item_categories ?? [])
    .map((row: any) => row.library_categories)
    .filter(Boolean) as Array<{ id: string; name: string; color: string }>;
  const returnTo = searchParams.get("returnTo") ?? "";

  const backFilters = (() => {
    const next = new URLSearchParams();
    const q = searchParams.get("q");
    const tag = searchParams.get("tag");
    const topic = searchParams.get("topic");
    const libraryItem = searchParams.get("libraryItem");
    if (q) next.set("q", q);
    if (tag) next.set("tag", tag);
    if (topic) next.set("topic", topic);
    if (libraryItem) next.set("libraryItem", libraryItem);
    const s = next.toString();
    return s ? `?${s}` : "";
  })();
  const detailForwardQuery = (() => {
    const next = new URLSearchParams();
    const q = searchParams.get("q");
    const tag = searchParams.get("tag");
    const topic = searchParams.get("topic");
    const libraryItem = searchParams.get("libraryItem");
    if (q) next.set("q", q);
    if (tag) next.set("tag", tag);
    if (topic) next.set("topic", topic);
    if (libraryItem) next.set("libraryItem", libraryItem);
    if (returnTo) next.set("returnTo", returnTo);
    const s = next.toString();
    return s ? `?${s}` : "";
  })();
  const hasFilters = backFilters.length > 0;
  const returnHref = resolveReturnToHref(returnTo);
  const hasReturnToLibrary = Boolean(returnTo) && returnHref.startsWith("/my-library");
  const returnToExcerptViewHref = returnTo.startsWith("excerpts") ? returnHref : null;
  const returnToLibraryHref = hasReturnToLibrary ? returnHref : null;
  const sourceDetailHref = sourceRecord ? `/my-library?item=${sourceRecord.id}&returnTo=${encodeURIComponent(returnTo || `my-library:${sourceRecord.id}`)}` : null;

  return (
    <>
    <AppSeo title="Excerpt" />
    <div className="max-w-3xl space-y-6">
      {returnToExcerptViewHref ? (
        <Link to={returnToExcerptViewHref}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4" /> Return to my source/excerpt view
          </Button>
        </Link>
      ) : hasFilters ? (
        <Link to={`/excerpts${backFilters}`}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4" /> Back to search results
          </Button>
        </Link>
      ) : hasReturnToLibrary && returnToLibraryHref ? (
        <Link to={returnToLibraryHref}>
          <Button variant="ghost" size="sm">
            <ArrowLeft className="h-4 w-4" /> Back to source detail
          </Button>
        </Link>
      ) : (
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
      )}
      <Card className="shadow-soft">
        <CardContent className="space-y-6 py-8">
          <div className="flex justify-end">
            <ReadingFontToggle />
          </div>
          <div
            className="text-2xl leading-relaxed"
            dir="auto"
            dangerouslySetInnerHTML={{ __html: highlightHtml(item.body_html, tokens) }}
          />
          <div className="space-y-1 text-sm text-muted-foreground">
            {item.library_items?.author_text && (
              <div>
                <span
                  className="font-semibold text-foreground"
                  dangerouslySetInnerHTML={{ __html: highlightText(item.library_items.author_text, tokens) }}
                />
              </div>
            )}
            {(item.library_items?.display_title || item.library_items?.title) && (
              <div>
                <em
                  dangerouslySetInnerHTML={{
                    __html: highlightText(item.library_items.display_title ?? item.library_items.title ?? "", tokens),
                  }}
                />
                {item.page_start && ` · p.${item.page_start}${item.page_end ? `–${item.page_end}` : ""}`}
              </div>
            )}
            {(() => { const u = safeHref(item.source_url); return u ? <a href={u} target="_blank" rel="noreferrer" className="block text-primary hover:underline">{u}</a> : null; })()}
          </div>
          {sourceRecord ? (
            <div className="rounded-md border border-border bg-muted/20 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Source record</p>
                  <p className="text-sm text-muted-foreground">{deriveLibraryTitle(sourceRecord)}{sourceRecord.author_text ? ` — ${sourceRecord.author_text}` : ""}</p>
                </div>
                <div className="flex flex-wrap gap-2">
              <Link to={`/excerpts?libraryItem=${sourceRecord.id}&returnTo=${encodeURIComponent(returnTo || `my-library:${sourceRecord.id}`)}`}>
                    <Button variant="outline"><ArrowLeft className="h-4 w-4" /> View related excerpts</Button>
                  </Link>
                  {sourceDetailHref ? (
                    <Link to={sourceDetailHref}>
                      <Button variant="outline"><ArrowUpRight className="h-4 w-4" /> Open in My Library</Button>
                    </Link>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
          {inheritedCategories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {inheritedCategories.map((c) => (
                <LibraryCategoryBadge key={c.id} category={c} />
              ))}
            </div>
          )}
          {(tags.length > 0 || (active?.codex_enabled !== false && topics.length > 0)) && (
            <div className="flex flex-wrap gap-2">
              {active?.codex_enabled !== false &&
                topics.map((c: string) => (
                  <Badge
                    key={`tp-${c}`}
                    variant="outline"
                    onClick={() => navigate(`/excerpts?topic=${encodeURIComponent(c)}`)}
                    className="cursor-pointer border-primary bg-primary/10 text-primary hover:bg-primary/20"
                  >
                    {c}
                  </Badge>
                ))}
              {tags.map((t: string) => (
                <Badge
                  key={`t-${t}`}
                  variant="outline"
                  onClick={() => navigate(`/excerpts?tag=${encodeURIComponent(t)}`)}
                  className="cursor-pointer border-sage/50 bg-sage/15 text-foreground hover:bg-sage/25"
                >
                  {t}
                </Badge>
              ))}
            </div>
          )}
          {item.notes && (
            <div
              className="whitespace-pre-wrap border-l-2 border-accent pl-4 text-sm text-muted-foreground"
              dir="auto"
              dangerouslySetInnerHTML={{ __html: highlightText(item.notes, tokens) }}
            />
          )}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={useInImageStudio}><ImageIcon className="h-4 w-4" /> Make image</Button>
            <Link to={`/excerpts/${id}/edit${detailForwardQuery}`}><Button variant="outline"><Edit className="h-4 w-4" /> Edit</Button></Link>
            <Button
              variant="outline"
              disabled={duplicating || !active}
              onClick={async () => {
                if (!active || !id) return;
                setDuplicating(true);
                try {
                  const newId = await duplicateExcerpt(id, active.id);
                  toast.success("Duplicated");
                  navigate(`/excerpts/${newId}`);
                } catch (err: any) {
                  toast.error(err.message ?? "Failed to duplicate");
                } finally {
                  setDuplicating(false);
                }
              }}
            >
              <Copy className="h-4 w-4" /> {duplicating ? "Duplicating…" : "Duplicate"}
            </Button>
            <Button variant="ghost" onClick={remove}><Trash2 className="h-4 w-4" /> Delete</Button>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default ExcerptDetail;